import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';

import { api } from '../services/api';

const BASE = '/api/v1/fechamento-contabil-mensal';

interface IApiEnvelope<T> {
  data: T;
}

export type Parecer =
  | 'bloquear'
  | 'em_aberto'
  | 'liberar'
  | 'liberar_com_ressalvas'
  | 'sem_movimento';
export type Etapa = 'fiscal' | 'folha' | 'banco' | 'consolidacao';
export type EstadoEtapa = 'ok' | 'pendente' | 'sem_dado' | 'parcial';
export type Severidade = 'critica' | 'alta' | 'media' | 'baixa' | 'informativa';
export type Fila = '' | 'bloquear' | 'em_aberto' | 'liberar';
export type Parada = '' | Etapa | 'concluidas';
/** Marcas do fechamento: como o mês foi fechado, o que falta levar ao Domínio e o sem movimento. */
export type Marca =
  | ''
  | 'sem_movimento'
  | 'sem_movimento_exercicio'
  | 'sem_movimento_mes'
  | 'fechadas'
  | 'fechadas_pelo_sistema'
  | 'txt_pendente'
  | 'sistema_proprio';
/** sem_movimento: nada no mês nem no exercício; mes_parado: só o mês parado. */
export type TipoSemMovimento = 'sem_movimento' | 'mes_parado';

export interface IEtapaDoMes {
  estado: EstadoEtapa;
  motivo: string;
}

export interface IMesDaEmpresa {
  competencia: string; // AAAA-MM
  status: Parecer;
  criticas: number;
  altas: number;
  ajustes: number;
  receita: number | null;
  lucro: number | null;
  etapas: Record<Etapa, IEtapaDoMes> | null;
  tem_detalhe: boolean;
  fechada: boolean;
  /** os insumos mudaram depois de a competência ser fechada */
  alterada: boolean;
  /** fechada pelo sistema, por falta de movimento */
  fechada_pelo_sistema: boolean;
  /** fechada com ajuste para o Domínio e sem arquivo gerado */
  txt_pendente: boolean;
  sem_movimento: TipoSemMovimento | null;
  /** fecha em sistema próprio (BRDATA, Sankhya): aqui é só controle, sem TXT */
  sistema_proprio?: string | null;
}

export interface IEtapaDaEmpresa extends IEtapaDoMes {
  ok: number;
  pendente: number;
  sem_dado: number;
}

export interface ILinhaEmpresa {
  id: number; // id interno da empresa
  external_id: number; // código no Domínio
  razao: string;
  cnpj: string | null;
  /** fecha em sistema próprio (BRDATA, Sankhya), pela tag do cadastro */
  sistema_proprio?: string | null;
  revisoes: number;
  criticas: number;
  altas: number;
  receita: number;
  lucro: number;
  bloqueadas: number;
  em_aberto: number;
  liberadas: number;
  sem_movimento: number;
  fechadas: number;
  fechadas_pelo_sistema: number;
  txt_pendentes: number;
  meses: IMesDaEmpresa[];
  etapas: Record<Etapa, IEtapaDaEmpresa>;
  parou_em: Etapa | 'concluidas';
  situacao: Parecer;
}

export interface IPainelCarteira {
  total: number;
  fila: { chave: Exclude<Fila, ''>; empresas: number; meses: number; receita: number }[];
  trilha: Record<Etapa | 'concluidas', number>;
  ranque: { id: number; external_id: number; razao: string; criticas: number; altas: number }[];
  marcas: Record<Exclude<Marca, ''>, { empresas: number; meses: number }>;
}

export interface IOQueFalta {
  id: number;
  external_id: number;
  empresa: string;
  codigo: string;
  titulo: string;
  severidade: Severidade;
  modulo: string;
  competencia: string;
  vezes: number;
  pendencias: number;
  distintos: number;
  meses: number;
  valor: number;
}

export interface IProblema {
  codigo: string;
  titulo: string;
  severidade: Severidade;
  modulo: string;
  ocorrencias: number;
  valor: number;
  qtd_empresas: number;
  // na carteira o problema vai só com a contagem; a lista de empresas não é enviada
  empresas?: {
    id: number;
    external_id: number;
    razao: string;
    ocorrencias: number;
    valor: number;
    competencias: string[];
  }[];
}

/** Situação do mês no calendário e nos cards: fechada, liberada, divergência, travada. */
export type Situacao = 'fech' | 'lib' | 'div' | 'trav';
export type SubSituacao =
  | 'sistema'
  | 'area'
  | 'liberar'
  | 'sem_movimento'
  | 'sem_txt'
  | 'txt_gerado'
  | 'criticos'
  | 'altos';
/** Códigos do filtro de regime (a regra é a da Auditoria Inteligente). */
export type CodigoRegime = 'SN' | 'LP' | 'LR' | 'OUTRO';
export type TipoBusca = 'razao' | 'cnpj' | 'id';

export interface IGrupoDaSituacao {
  qtd: number;
  subs: Partial<Record<SubSituacao, number>>;
}

/** Mês de referência do card: o mais recente do período (ou dos marcados no calendário). */
export interface IReferencia {
  competencia: string;
  situacao: Situacao;
  subs: SubSituacao[];
  etapas: Record<Etapa, IEtapaDoMes> | null;
}

export interface ICompetenciaDoCard {
  competencia: string;
  situacao: Situacao;
  subs: SubSituacao[];
}

/**
 * Um card por empresa, com as competências do recorte (da mais recente para a mais
 * antiga). Receita, críticas, altas e txt_pendentes somam esses meses.
 */
export interface ICardDaCarteira extends ILinhaEmpresa {
  regime: string;
  referencia: IReferencia;
  competencias: ICompetenciaDoCard[];
}

export interface IContagens {
  total: number;
  situacoes: Record<Situacao, IGrupoDaSituacao>;
  sistema_proprio: number;
  txt_pendente: number;
  inativas: number;
  /** chips tirados pelo x: quanto contavam antes de sair (os demais contam o que sobrou) */
  tirados: Record<string, number>;
}

export interface ICarteira {
  periodo: [string, string];
  painel: IPainelCarteira;
  /** empresas (cards) no recorte */
  total: number;
  /** competências somadas dos cards */
  total_competencias: number;
  pagina: number;
  paginas: number;
  empresas: ICardDaCarteira[];
  o_que_falta: { total: number; itens: IOQueFalta[] };
  por_problema: { total: number; itens: IProblema[] };
  contagens: IContagens;
}

export interface IFiltroCarteira {
  ini: string; // AAAA-MM
  fim: string;
  fila?: Fila;
  etapa?: Parada;
  busca?: string;
  tipoBusca?: TipoBusca;
  marca?: Marca;
  /** códigos do Domínio das empresas escolhidas no campo de empresas */
  empresas?: number[];
  ordem?: 'receita' | 'criticos';
  pagina?: number;
  /** período de vários anos: os meses (AAAA-MM), que não formam um intervalo contínuo */
  meses?: string[];
  regimes?: CodigoRegime[];
  situacao?: Situacao | '';
  sub?: SubSituacao | '';
  /** meses marcados no calendário: AAAA-MM:situação */
  selecao?: string[];
  /** chips tirados da lista pelo x: situações, sistema_proprio, txt_pendente */
  excluir?: string[];
}

const paramsDaCarteira = (filtro: IFiltroCarteira): Record<string, string | number> => {
  const params: Record<string, string | number> = { ini: filtro.ini, fim: filtro.fim };
  if (filtro.fila) params.fila = filtro.fila;
  if (filtro.etapa) params.etapa = filtro.etapa;
  if (filtro.busca) params.busca = filtro.busca;
  if (filtro.busca && filtro.tipoBusca) params.tipo_busca = filtro.tipoBusca;
  if (filtro.marca) params.marca = filtro.marca;
  if (filtro.empresas?.length) params.empresas = filtro.empresas.join(',');
  if (filtro.ordem) params.ordem = filtro.ordem;
  if (filtro.pagina) params.pagina = filtro.pagina;
  if (filtro.meses?.length) params.meses = filtro.meses.join(',');
  if (filtro.regimes?.length) params.regimes = filtro.regimes.join(',');
  if (filtro.situacao) params.situacao = filtro.situacao;
  if (filtro.sub) params.sub = filtro.sub;
  if (filtro.selecao?.length) params.selecao = filtro.selecao.join(',');
  if (filtro.excluir?.length) params.excluir = filtro.excluir.join(',');
  return params;
};

export interface IMesDoCalendario {
  competencia: string;
  total: number;
  inativas: number;
  pareceres: Record<Parecer, number>;
  situacoes: Record<Situacao, IGrupoDaSituacao>;
}

export interface ICalendario {
  ano: number;
  anos: number[];
  meses: IMesDoCalendario[];
  /** empresas do ano em cada regime (rótulo -> quantidade) */
  regimes: Record<string, number>;
}

/** Os doze meses do ano: empresas, inativas, pareceres e as quatro situações. */
export const useFechamentoCalendario = (ano: number, regimes: CodigoRegime[]) =>
  useQuery({
    queryKey: ['fechamento-contabil-mensal', 'calendario', ano, regimes] as const,
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<ICalendario> => {
      const params: Record<string, string | number> = { ano };
      if (regimes.length) params.regimes = regimes.join(',');
      const res = await api.get<IApiEnvelope<ICalendario>>(`${BASE}/calendario`, { params });
      return res.data.data;
    },
  });

export interface IEmpresaInativa {
  id: number;
  external_id: number;
  razao: string;
  cnpj: string | null;
  regime: string;
  competencia: string;
  /** inativa no cadastro, ou ativa mas fora do período em que é cliente */
  motivo: 'inativa' | 'fora_rt';
  cliente_desde: string | null;
  cliente_ate: string | null;
}

export interface IInativas {
  periodo: [string, string];
  total: number;
  pagina: number;
  paginas: number;
  empresas: IEmpresaInativa[];
}

/** Empresas com balancete que a revisão não considera (inativas ou fora do RT). */
export const useFechamentoInativas = (filtro: IFiltroCarteira, ativo: boolean) =>
  useQuery({
    queryKey: ['fechamento-contabil-mensal', 'inativas', filtro] as const,
    enabled: ativo,
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<IInativas> => {
      const todos = paramsDaCarteira(filtro);
      const params: Record<string, string | number> = { ini: filtro.ini, fim: filtro.fim };
      ['meses', 'regimes', 'busca', 'tipo_busca', 'pagina'].forEach(k => {
        if (todos[k] != null) params[k] = todos[k];
      });
      const res = await api.get<IApiEnvelope<IInativas>>(`${BASE}/carteira/inativas`, { params });
      return res.data.data;
    },
  });

export interface IEmpresaSugerida {
  id: number;
  external_id: number;
  razao: string;
  cnpj: string | null;
}

/** Até 20 empresas com revisão no período, pelo código, pela razão social ou pelo CNPJ. */
export const buscarEmpresas = async (
  termo: string,
  ini: string,
  fim: string,
): Promise<IEmpresaSugerida[]> => {
  const res = await api.get<IApiEnvelope<IEmpresaSugerida[]>>(`${BASE}/empresas-busca`, {
    params: { termo, ini, fim },
  });
  return res.data.data;
};

export const useFechamentoBuscaEmpresas = (termo: string, ini: string, fim: string) =>
  useQuery({
    queryKey: ['fechamento-contabil-mensal', 'empresas-busca', termo, ini, fim] as const,
    enabled: termo.trim().length >= 2 || /^\d+$/.test(termo.trim()),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
    queryFn: () => buscarEmpresas(termo.trim(), ini, fim),
  });

/**
 * Baixa o Excel com a relação de empresas por competência, no mesmo recorte da carteira
 * (sem página e sem ordem), para comparar com outro sistema.
 */
export const baixarRelacaoDaCarteira = async (filtro: IFiltroCarteira): Promise<void> => {
  const params = paramsDaCarteira(filtro);
  delete params.pagina;
  delete params.ordem;
  const res = await api.get<Blob>(`${BASE}/carteira/relacao`, {
    params,
    responseType: 'blob',
  });
  const doHeader = /filename="([^"]+)"/.exec(res.headers['content-disposition'] ?? '')?.[1];
  const nome = doHeader || `fechamento_relacao_${filtro.ini}_a_${filtro.fim}.xlsx`;
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', nome);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/** Visão da carteira. Lê só os campos resumidos; nenhum detalhe é carregado. */
export const useFechamentoCarteira = (filtro: IFiltroCarteira) =>
  useQuery({
    queryKey: ['fechamento-contabil-mensal', 'carteira', filtro] as const,
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<ICarteira> => {
      const params = paramsDaCarteira(filtro);
      const res = await api.get<IApiEnvelope<ICarteira>>(`${BASE}/carteira`, { params });
      return res.data.data;
    },
  });

export interface IEmpresaNoPeriodo {
  periodo: [string, string];
  empresa: ILinhaEmpresa | null;
  o_que_falta: IOQueFalta[];
  por_problema: IProblema[];
}

/** Meses da empresa no período, com parecer, contagens e etapas. */
export const useFechamentoEmpresa = (empresaId: number | undefined, ini: string, fim: string) =>
  useQuery({
    queryKey: ['fechamento-contabil-mensal', 'empresa', empresaId, ini, fim] as const,
    enabled: empresaId != null,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<IEmpresaNoPeriodo> => {
      const res = await api.get<IApiEnvelope<IEmpresaNoPeriodo>>(`${BASE}/empresas/${empresaId}`, {
        params: { ini, fim },
      });
      return res.data.data;
    },
  });

/** Resposta de uma seção: os dados, ou o aviso de que a versão não tem detalhe. */
export interface ISecao<T> {
  disponivel: boolean;
  motivo?: string;
  competencia: string;
  versao: number;
  situacao: 'PROVISORIO' | 'FECHADO' | 'SUBSTITUIDO';
  apurado_em?: string;
  alterada_em?: string | null;
  dados?: T;
}

export interface IVerificacao {
  codigo: string;
  titulo: string;
  severidade: Severidade;
  detalhe: string;
  valor: number | null;
  contas: string[];
}

export interface IContaDoPlano {
  codigo: string;
  classificacao: string;
  nome: string;
}

export interface IPontoDaSerie {
  competencia: string;
  receita_bruta: number | null;
  lucro_liquido: number | null;
  disponivel: number | null;
}

export interface IResumo {
  empresa: {
    id: number;
    razao: string | null;
    cnpj: string | null;
    regime: string | null;
    regime_texto: string | null;
    cidade: string | null;
    uf: string | null;
    filiais: { empresa: number; razao: string | null; notas: number; consolidada: boolean }[];
    /** período de responsabilidade técnica do escritório, do cadastro (cliente desde / até) */
    rt?: { desde: string | null; ate: string | null; responsavel: string | null };
  };
  parecer: { status: Parecer; fonte: string; justificativa: string; motivos_codigo: string[] };
  criticas: number;
  altas: number;
  ajustes: number;
  efeito_ajustes: number | null;
  base: string | null;
  motor_versao: string;
  materialidade: number;
  serie: IPontoDaSerie[];
  dre: { mes: Record<string, number>; ano: Record<string, number>; ordem: string[] };
  limitacoes: string[];
  consistencia: { item: string; ok: boolean | null; obs: string }[];
  caixa: number | null;
  indicadores_chave: Record<
    string,
    { valor: number | null; anterior: number | null; distorcido_por: string[] }
  >;
  cruzamentos_resumo: { feitos: number; conferem: number; comparaveis: number };
  contagem: Record<Severidade, number>;
  principais: Pick<IVerificacao, 'codigo' | 'titulo' | 'severidade' | 'detalhe' | 'valor'>[];
}

export interface ICruzamento {
  numero: number;
  nome: string;
  titulo_md: string;
  contas: string[];
  situacao: string;
  rotulo_a: string;
  valor_a: number | null;
  rotulo_b: string;
  valor_b: number | null;
  diferenca: number | null;
  observacao: string;
  /** linhas do e-CAC por trás do valor pago (cruzamentos 3 e 10) */
  guias?: IGuiaDoCruzamento[];
  /** cruzamento 3: a apuração e a contabilização do mês abertas em linhas */
  quadros?: IQuadroDoCruzamento[];
}

/** debito, credito, saldo (credor de PIS/COFINS), ajuste e resultado (a linha da conta) */
export type TipoDaLinhaDoQuadro = 'debito' | 'credito' | 'saldo' | 'ajuste' | 'resultado';

export interface IQuadroDoCruzamento {
  titulo: string;
  /** de onde vêm os números: conta, raiz do CNPJ, desde quando o saldo credor é somado */
  nota: string;
  linhas: { rotulo: string; valor: number; tipo: TipoDaLinhaDoQuadro }[];
}

/** competencia: entra no pago; parcelamento e outro_periodo ficam fora da comparação */
export type ClasseDaGuia = 'competencia' | 'parcelamento' | 'outro_periodo';

export interface IGuiaDoCruzamento {
  data: string;
  documento: string | null;
  codigo: string;
  descricao: string | null;
  principal: number;
  multa: number;
  juros: number;
  total: number;
  classe: ClasseDaGuia;
}

export interface ICruzamentos {
  cruzamentos: ICruzamento[];
  cruzamentos_sem_dado: { numero: number; titulo: string; situacao: string; motivo: string }[];
}

export interface IContaConciliada {
  conta: string;
  nome: string;
  alerta: string;
  extratos: string[];
  fonte_saldo: string;
  reclassificacoes: number;
  saldo_contabil: number | null;
  saldo_extrato: number | null;
  diferenca: number | null;
  entradas_razao: number;
  entradas_extrato: number;
  saidas_razao: number;
  saidas_extrato: number;
  pendentes_qtd: number;
}

export interface ISaldoDeConta {
  conta: string;
  nome: string;
  saldo: number;
}

export interface IConciliacao {
  disponivel: boolean;
  contas: IContaConciliada[];
  sem_extrato: ISaldoDeConta[];
  contas_bancarias?: ISaldoDeConta[];
  sem_lancamento?: { itens: number; valor: number };
  pendentes_por_categoria?: {
    categoria: string;
    nome: string;
    itens: number;
    valor: number;
    sem_extrato: number;
    sem_lancamento: number;
  }[];
}

export interface IItemSemPar {
  data: string;
  lado: 'extrato' | 'razao';
  conta: string;
  conta_nome: string;
  valor: number;
  sentido: 'E' | 'S';
  categoria: string;
  historico: string;
}

export interface IPendentes {
  total: number;
  valor: number;
  pagina: number;
  paginas: number;
  itens: IItemSemPar[];
}

export type Dre = Record<string, number>;

export interface IGruposDoBalanco {
  ativo_total: number;
  ativo_circulante: number;
  ativo_nao_circulante: number;
  disponivel: number;
  passivo_total: number;
  passivo_circulante: number;
  passivo_nao_circulante: number;
  patrimonio_liquido: number;
  resultado_do_periodo: number;
  diferenca: number;
  subgrupos: { grupo: string; subgrupo: string; valor: number }[];
}

export interface ILinhaDoBalanco {
  coluna: 'ativo' | 'passivo';
  secao: string;
  linha: string;
  atual: number;
  anterior: number | null;
  contas: { codigo: string; atual: number; anterior: number | null }[];
}

export interface IContaDaDre {
  codigo: string;
  nome: string;
  linha: string;
  mes: number;
  ano: number;
  grupo_sintetico: string | null;
  grupo_sintetico_nome: string | null;
}

export interface IContaDoDfc {
  codigo: string;
  nome: string;
  categoria: string;
  subgrupo: string | null;
  valor: number;
}

export interface IDemonstracoes {
  dre: { mes: Dre; ano: Dre; ordem: string[] };
  dre_auditada: Dre | null;
  dre_contas: IContaDaDre[];
  balanco: {
    atual: IGruposDoBalanco;
    anterior: Partial<IGruposDoBalanco> | null;
    modelo: ILinhaDoBalanco[];
  };
  dfc: Record<string, number>;
  dfc_contas: IContaDoDfc[];
}

/** DRE e fluxo de caixa somados de `de` a `ate`; o servidor só soma o que cada mês gravou. */
export interface IDemonstracoesDoPeriodo {
  de: string;
  ate: string;
  /** meses que entraram na soma */
  meses: string[];
  /** meses do intervalo sem apuração (ou sem detalhe), fora da soma */
  faltam: string[];
  dados: Pick<IDemonstracoes, 'dre' | 'dre_contas' | 'dfc' | 'dfc_contas'> | null;
  /** os mesmos números mês a mês, para as colunas da tela */
  por_mes: IMesDoPeriodo[];
  /** balanço do mês anterior a `de`, para o comparativo */
  balanco_anterior: { balanco: IGruposDoBalanco | null; modelo: ILinhaDoModelo[] } | null;
}

/** Linha do balanço no layout da planilha, só com o saldo do fim. */
export interface ILinhaDoModelo {
  coluna: 'ativo' | 'passivo';
  secao: string;
  linha: string;
  atual: number;
}

/** Metade de um mês (1–15 ou 16–fim), calculada na revisão a partir do razão. */
export interface IQuinzena {
  inicio: string;
  fim: string;
  dre: Dre;
  dfc: Record<string, number>;
  balanco: IGruposDoBalanco;
  /** efeito de cada conta da DRE na quinzena */
  dre_contas: Record<string, number>;
  /** só na 1ª: o balanço do dia 15 no layout da planilha */
  modelo?: ILinhaDoModelo[];
}

export interface IMesDoPeriodo {
  competencia: string;
  dre: Dre | null;
  dre_contas: Record<string, number>;
  dfc: Record<string, number> | null;
  balanco: IGruposDoBalanco | null;
  modelo: ILinhaDoModelo[];
  /** null: apurado antes da quinzena; vazio: mês sem razão, não dá para partir */
  quinzenas: IQuinzena[] | null;
}

const buscarPeriodo = async (
  empresaId: number | undefined,
  de: string,
  ate: string,
): Promise<IDemonstracoesDoPeriodo> => {
  const res = await api.get<IApiEnvelope<IDemonstracoesDoPeriodo>>(
    `${BASE}/empresas/${empresaId}/periodo/demonstracoes`,
    { params: { de, ate } },
  );
  return res.data.data;
};

/** O mesmo intervalo de meses em cada ano marcado, uma chamada por ano. */
export const useDemonstracoesDosAnos = (
  empresaId: number | undefined,
  anos: number[],
  mesDe: string,
  mesAte: string,
) =>
  useQueries({
    queries: anos.map(ano => ({
      queryKey: [
        'fechamento-contabil-mensal',
        'periodo',
        empresaId,
        `${ano}-${mesDe}`,
        `${ano}-${mesAte}`,
      ] as const,
      enabled: empresaId != null,
      staleTime: 5 * 60 * 1000,
      queryFn: () => buscarPeriodo(empresaId, `${ano}-${mesDe}`, `${ano}-${mesAte}`),
    })),
  });

export const useFechamentoPeriodo = (empresaId: number | undefined, de: string, ate: string) =>
  useQuery({
    queryKey: ['fechamento-contabil-mensal', 'periodo', empresaId, de, ate] as const,
    enabled: empresaId != null && !!de && de < ate,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<IDemonstracoesDoPeriodo> => {
      const res = await api.get<IApiEnvelope<IDemonstracoesDoPeriodo>>(
        `${BASE}/empresas/${empresaId}/periodo/demonstracoes`,
        { params: { de, ate } },
      );
      return res.data.data;
    },
  });

/** Uma verificação de um mês do período, só com o que o quadro "No período" usa. */
export interface IConstatacaoDoPeriodo {
  competencia: string;
  codigo: string;
  titulo: string;
  severidade: Severidade;
  valor: number | null;
}

export const useFechamentoConstatacoesDoPeriodo = (
  empresaId: number | undefined,
  de: string,
  ate: string,
  enabled = true,
) =>
  useQuery({
    queryKey: ['fechamento-contabil-mensal', 'periodo-constatacoes', empresaId, de, ate] as const,
    enabled: enabled && empresaId != null && !!de && de < ate,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<IConstatacaoDoPeriodo[]> => {
      const res = await api.get<IApiEnvelope<{ itens: IConstatacaoDoPeriodo[] }>>(
        `${BASE}/empresas/${empresaId}/periodo/constatacoes`,
        { params: { de, ate } },
      );
      return res.data.data.itens;
    },
  });

export interface IIndicador {
  nome: string;
  grupo: string;
  formula: string;
  leitura: string | null;
  valor: number | null;
  anterior: number | null;
  distorcido_por: string[];
  serie: { competencia: string; valor: number | null }[];
}

export interface IIndicadores {
  indicadores: IIndicador[];
  serie: (Record<string, number | null> & { competencia: string })[];
}

export interface IAjuste {
  id: number;
  sequencia: number;
  tipo: string;
  conta_debito: string | null;
  nome_debito: string | null;
  conta_credito: string | null;
  nome_credito: string | null;
  valor: number;
  data_sugerida: string | null;
  historico: string;
  base_legal: string | null;
  codigo_achado: string | null;
  confianca: string;
  efeito_lucro: number | null;
  situacao: 'PROPOSTO' | 'ACEITO' | 'RECUSADO' | 'LANCADO';
  decidido_por: string | null;
  decidido_em: string | null;
  observacao: string | null;
  exportado_em: string | null;
  exportado_por: string | null;
  /** por que o ajuste não pode ir para o arquivo do Domínio; null quando pode */
  motivo_fora: string | null;
}

export interface IEventoDoFechamento {
  evento: 'fechado' | 'reaberto' | 'txt_gerado';
  /** manual: alguém da área; sistema: fechamento ou reabertura automáticos */
  origem?: 'manual' | 'sistema';
  por: string;
  nome?: string | null;
  em: string;
  /** versão da apuração em que o evento aconteceu */
  versao?: number;
  justificativa?: string | null;
  motivo?: string | null;
  /** no fechamento automático: o perfil da competência sem movimento */
  tipo?: TipoSemMovimento | null;
  parecer?: Parecer;
  criticas?: number;
  altas?: number;
  /** ajustes que podiam ir ao Domínio e ainda não tinham ido, na hora de fechar */
  para_txt?: number;
  /** na geração do arquivo */
  incluidos?: number;
  valor?: number;
  reexportado?: boolean;
  alterada_em?: string | null;
}

export interface ISituacaoDoFechamento {
  versao: number;
  situacao: 'PROVISORIO' | 'FECHADO';
  parecer: Parecer;
  criticas: number;
  altas: number;
  alterada_em: string | null;
  /** aberta e calculada por uma regra anterior do motor: a revisão da carteira ainda vai atualizá-la */
  regra_anterior?: boolean;
  /** fecha em sistema próprio (BRDATA, Sankhya): aqui é só controle, sem TXT */
  sistema_proprio?: string | null;
  /** o último fechamento da apuração vigente, quando está fechada */
  fechamento: IEventoDoFechamento | null;
  /** por que não pode fechar agora (mês anterior aberto ou sem revisão); null quando pode */
  bloqueio_fechar: string | null;
  /** por que não pode reabrir agora (mês seguinte fechado); null quando pode */
  bloqueio_reabrir: string | null;
  /** meses seguintes, fechados pelo sistema, que reabrem junto (AAAA-MM) */
  reabrem_junto: string[];
  /** eventos de todas as versões da competência, do mais antigo ao mais novo */
  historico: IEventoDoFechamento[];
  txt: {
    pendente: boolean;
    /** ajustes que entram no próximo arquivo */
    a_gerar: number;
    /** ajustes que o Domínio não importaria (um lado só, conta inválida...) */
    fora: number;
    exportados: number;
  };
}

export interface ITxtDoDominio {
  conteudo: string | null;
  nome_arquivo?: string;
  incluidos: number;
  valor: number;
  fora: { id: number; sequencia: number; motivo: string }[];
}

export interface IFiltroPendentes {
  lado?: 'extrato' | 'razao';
  categoria?: string;
  conta?: string;
  pagina: number;
}

/** Itens sem par da conciliação, uma página por chamada, filtrados no servidor. */
export const useFechamentoPendentes = (
  empresaId: number | undefined,
  competencia: string | undefined,
  filtro: IFiltroPendentes,
  enabled = true,
) =>
  useQuery({
    queryKey: ['fechamento-contabil-mensal', 'pendentes', empresaId, competencia, filtro] as const,
    enabled: enabled && empresaId != null && !!competencia,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<ISecao<IPendentes>> => {
      const params: Record<string, string | number> = { pagina: filtro.pagina, por_pagina: 50 };
      if (filtro.lado) params.lado = filtro.lado;
      if (filtro.categoria) params.categoria = filtro.categoria;
      if (filtro.conta) params.conta = filtro.conta;
      const res = await api.get<IApiEnvelope<ISecao<IPendentes>>>(
        `${BASE}/empresas/${empresaId}/${competencia}/conciliacao/pendentes`,
        { params },
      );
      return res.data.data;
    },
  });

/**
 * Uma seção da revisão por chamada. Cada tela busca só a sua, quando o
 * usuário abre; o que já foi aberto fica em cache.
 */
export const useFechamentoSecao = <T>(
  secao: string,
  empresaId: number | undefined,
  competencia: string | undefined,
  enabled = true,
) =>
  useQuery({
    queryKey: ['fechamento-contabil-mensal', 'secao', secao, empresaId, competencia] as const,
    enabled: enabled && empresaId != null && !!competencia,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<ISecao<T>> => {
      const res = await api.get<IApiEnvelope<ISecao<T>>>(
        `${BASE}/empresas/${empresaId}/${competencia}/${secao}`,
      );
      return res.data.data;
    },
  });
